import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { quickReferService } from '../api/quickrefer/QuickReferService';
import { QuickRefer } from '../api/quickrefer/types/QuickRefer.types';
import '../styles/ReferralsList.css';

interface ReferralsListProps {
}

type SortField = 'pas_application_id' | 'underwriter' | 'named_insured' | 'accelerant_status' | 'created';
type SortDirection = 'asc' | 'desc';

const ReferralsList: React.FC<ReferralsListProps> = () => {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState<QuickRefer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const { policySystem } = useParams<{ policySystem: string }>();
  
  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = policySystem 
        ? await quickReferService.getReferrals(policySystem)
        : await quickReferService.getReferrals('surefyre');
      
      if (result.isOk && result.value) {
        setReferrals(result.value);
      } else if (result.isErr) {
        setError(result.error?.message || 'Failed to fetch referrals');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [policySystem]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRowClick = (referral: QuickRefer) => {
    navigate(`/${policySystem}/application/${referral.pas_application_id}`);
  };

  const handleAccelerantClick = (e: React.MouseEvent, referralId: string) => {
    e.stopPropagation();
    window.open(`https://app.accelerant.ai/underwriting-referrals/${referralId}`, '_blank');
  };

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortField(field);
    setSortDirection(isAsc ? 'desc' : 'asc');
  };

  const getSortedReferrals = () => {
    if (!sortField) return referrals;

    return [...referrals].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'pas_application_id':
          aValue = a.pas_application_id;
          bValue = b.pas_application_id;
          break;
        case 'underwriter':
          aValue = a.data?.underwriter || '';
          bValue = b.data?.underwriter || '';
          break;
        case 'named_insured':
          aValue = a.data?.named_insured || '';
          bValue = b.data?.named_insured || '';
          break;
        case 'accelerant_status':
          aValue = a.accelerant_status || '';
          bValue = b.accelerant_status || '';
          break;
        case 'created':
          aValue = a.history?.[0]?.submittedAt ? new Date(a.history[0].submittedAt).getTime() : 0;
          bValue = b.history?.[0]?.submittedAt ? new Date(b.history[0].submittedAt).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return '↕️';
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="referrals-list-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading referrals...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="referrals-list-container">
          <div className="error-state">
            <h2>Error Loading Referrals</h2>
            <p>{error}</p>
            <button onClick={fetchReferrals} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="referrals-list-container">
        <div className="referrals-header">
          <h1>{policySystem === 'rise' ? 'Rise' : 'Surefyre'} Referrals</h1>
          <p className="referrals-count">{referrals.length} referral{referrals.length !== 1 ? 's' : ''} found</p>
        </div>

        {referrals.length === 0 ? (
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 2L3 7V19C3 19.5304 3.21071 20.0391 3.58579 20.4142C3.96086 20.7893 4.46957 21 5 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V7L15 2H9Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 2V7H15" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>No Referrals Yet</h3>
            <p>Referrals will appear here once they are created.</p>
          </div>
        ) : (
          <div className="referrals-table-wrapper">
            <table className="referrals-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('pas_application_id')}>
                    Application ID <span className="sort-icon">{getSortIcon('pas_application_id')}</span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('underwriter')}>
                    Underwriter <span className="sort-icon">{getSortIcon('underwriter')}</span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('named_insured')}>
                    Named Insured <span className="sort-icon">{getSortIcon('named_insured')}</span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('accelerant_status')}>
                    Accelerant Status <span className="sort-icon">{getSortIcon('accelerant_status')}</span>
                  </th>
                  <th className="sortable" onClick={() => handleSort('created')}>
                    Created <span className="sort-icon">{getSortIcon('created')}</span>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSortedReferrals().map((referral) => (
                  <tr 
                    key={`${referral.pas}-${referral.pas_application_id}`}
                    onClick={() => handleRowClick(referral)}
                    className="clickable-row"
                  >
                    <td>{referral.pas_application_id}</td>
                    <td>{referral.data?.underwriter || 'N/A'}</td>
                    <td>{referral.data?.named_insured || 'N/A'}</td>
                    <td>{referral.accelerant_status || 'N/A'}</td>
                    <td>{formatDate(referral.history?.[0]?.submittedAt)}</td>
                    <td>
                      <div className="actions-cell">
                        {referral.accelerant_referral_id && (
                          <button
                            className="action-button accelerant-button"
                            onClick={(e) => handleAccelerantClick(e, referral.accelerant_referral_id!)}
                            title="View in Accelerant"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M15 3H21M21 3V9M21 3L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        )}
                        <button
                          className="action-button view-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(referral);
                          }}
                          title="View Application"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default ReferralsList;